import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { type DateRange } from "@src/appV2/lib";
import { getDaylightSavingsTimeIndicator } from "@src/appV2/redesign/Shift/utils";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode } from "react";

import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";
import { TimeRangeLabel } from "../../DateTime/TimeRangeLabel";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { WorkplaceNameWithDistance } from "./WorkplaceNameWithDistance";

interface ShiftSummaryInfoProps {
  dateRange: DateRange;
  deviceTimezone: string;
  durationInHours?: number;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
  workplaceName: string;
  formattedDistance?: string;
  timezone?: string;
  sx?: SxProps<Theme>;
  showTimeRange?: boolean;
  useNowForStartDate?: boolean;
  /**
   * Optional children to render in the summary info
   * Used to render pills, above the summary info content
   */
  children?: ReactNode;
}

/**
 * Renders summary about the open shift
 * - Date of the shift
 * - Time of the shift with duration
 * - Workplace name and distance
 *
 * Also includes daylight savings time indicator if applicable
 *
 * @deprecated Use `ShiftCardSummaryInfo` instead
 */
export function ShiftCardSummaryInfoDeprecated(props: ShiftSummaryInfoProps) {
  const {
    dateRange,
    timezone,
    deviceTimezone,
    durationInHours,
    breakDurationInMinutes = 0,
    requiresBreak = false,
    workplaceName,
    formattedDistance,
    children,
    sx,
    showTimeRange = true,
    useNowForStartDate = false,
  } = props;

  const daylightSavingsTimeIndicator = getDaylightSavingsTimeIndicator({
    shift: { start: dateRange.startDate.toISOString(), end: dateRange.endDate.toISOString() },
    timeZone: timezone ?? "America/Los_Angeles",
  });

  return (
    <Stack gap={3} sx={sx}>
      {children}

      <Text semibold variant="h6">
        {formatDayOfMonthWithDayName(dateRange.startDate, { timezone })}
      </Text>

      {showTimeRange && (
        <Stack spacing={1}>
          <TimeRangeLabel
            semibold
            dateRange={dateRange}
            timezone={timezone}
            deviceTimezone={deviceTimezone}
            variant="h6"
            useNowForStartDate={useNowForStartDate}
          />

          <DurationWithBreakDisplay
            totalDurationInHours={durationInHours ?? 0}
            breakDurationInMinutes={breakDurationInMinutes}
            requiresBreak={requiresBreak}
          />
        </Stack>
      )}

      {isDefined(daylightSavingsTimeIndicator) && (
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
          {daylightSavingsTimeIndicator}
        </Text>
      )}

      <WorkplaceNameWithDistance
        textVariant="body1"
        workplaceName={workplaceName}
        formattedDistance={formattedDistance}
      />
    </Stack>
  );
}
